import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "input", "idleIcon", "recordingIcon", "stopLabel", "status", "statusText", "statusDot", "limitHint"]
  static values = { conversationId: String, endpoint: String }
  static MAX_RECORDING_SECONDS = 5 * 60

  connect() {
    this.disconnected = false
    this.recording = false
    this.mediaRecorder = null
    this.chunks = []
    this.selectedMimeType = this.detectMimeType()
    this.recordingStartedAt = null
    this.recordingTimer = null
    this.hitRecordingLimit = false
    this.voiceErrorActive = false
  }

  disconnect() {
    this.disconnected = true
    this.clearRecordingTimer()
    this.stopRecording()
  }

  detectMimeType() {
    if (typeof MediaRecorder === "undefined") return null

    const supportedTypes = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
    ]

    for (const type of supportedTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    return "audio/mp4" // fallback
  }

  async toggle() {
    if (this.recording) {
      this.stopRecording()
    } else {
      await this.startRecording()
    }
  }

  async startRecording() {
    if (this.hasInputTarget && this.inputTarget.disabled) return
    if (!this.selectedMimeType) {
      this.handleVoiceError("This browser can't record audio.", {
        stage: "start_recording",
        details: { reason: "mime_type_unsupported" },
      })
      return
    }

    this.voiceErrorActive = false

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (this.disconnected || (this.hasInputTarget && this.inputTarget.disabled)) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      this.chunks = []

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.selectedMimeType,
      })

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        // Stop all tracks to release the microphone
        stream.getTracks().forEach((track) => track.stop())
        if (!this.disconnected) this.processAudio()
      }

      this.mediaRecorder.onerror = (event) => {
        const message = event?.error?.message || "Recording failed unexpectedly."
        this.handleVoiceError("Recording stopped unexpectedly.", {
          stage: "media_recorder",
          details: {
            message,
            name: event?.error?.name,
          },
        })
      }

      this.mediaRecorder.start()
      this.recording = true
      this.recordingStartedAt = Date.now()
      this.startRecordingTimer()
      this.showLimitHint()
      this.showStatus(this.formatRecordingStatus(), { pulse: true, error: false })
      this.updateButtonState()
    } catch (error) {
      if (error.name === "NotAllowedError") {
        this.handleVoiceError("Microphone access is blocked.", {
          stage: "start_recording",
          details: { name: error.name, message: error.message },
        })
      } else {
        console.error("Failed to start recording:", error)
        this.handleVoiceError("Couldn't start the microphone.", {
          stage: "start_recording",
          details: { name: error.name, message: error.message },
        })
      }
    }
  }

  stopRecording({ reason = "manual" } = {}) {
    const wasRecording = this.mediaRecorder && this.mediaRecorder.state === "recording"

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop()
    }
    this.recording = false
    this.clearRecordingTimer()
    if (wasRecording) {
      this.hitRecordingLimit = (reason === "limit")
      this.showStatus(this.hitRecordingLimit ? "5:00 limit reached. Processing..." : "Processing...", { pulse: false, error: false })
    }
    this.updateButtonState()
  }

  async processAudio() {
    if (this.chunks.length === 0) return

    this.setProcessingState(true)

    const blob = new Blob(this.chunks, { type: this.selectedMimeType })
    const audioFormat = this.audioFormatFor(blob.type || this.selectedMimeType)
    this.chunks = []

    try {
      if (!audioFormat) {
        this.handleVoiceError("This browser saved audio in an unsupported format.", {
          stage: "audio_processing",
          details: {
            reason: "unsupported_audio_format",
            mimeType: blob.type || this.selectedMimeType,
          },
        })
        return
      }

      const base64data = await this.blobToBase64(blob)
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content

      const response = await fetch(
        this.endpointValue || `/conversations/${this.conversationIdValue}/transcriptions`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            audio_data: base64data,
            audio_format: audioFormat,
          }),
        }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.text && !this.isRefusalResponse(data.text)) {
          this.insertText(data.text)
        } else if (!data.text) {
          this.handleVoiceError("No speech was detected.", {
            stage: "transcription_response",
            details: { reason: "empty_text" },
          })
        } else {
          this.handleVoiceError("Couldn't understand the recording.", {
            stage: "transcription_response",
            details: { reason: "refusal_response" },
          })
        }
      } else {
        const error = await response.json().catch(() => ({}))
        const message = error.error || error.message || `HTTP ${response.status}`
        console.error("Transcription failed:", {
          status: response.status,
          statusText: response.statusText,
          message,
          error,
        })
        this.handleVoiceError(this.userFriendlyTranscriptionError(message, response.status), {
          stage: "transcription_request",
          details: {
            status: response.status,
            statusText: response.statusText,
            message,
          },
        })
      }
    } catch (error) {
      console.error("Transcription error:", error)
      this.handleVoiceError(this.userFriendlyProcessingError(error), {
        stage: "audio_processing",
        details: {
          name: error?.name,
          message: error?.message,
        },
      })
    } finally {
      this.setProcessingState(false)
      if (this.hitRecordingLimit) {
        this.showLimitHint()
        this.showStatus("5:00 max reached. You can record another part.", { pulse: false, error: false })
      } else if (this.voiceErrorActive) {
        // Keep the specific error visible so the user knows what happened.
      } else {
        this.hideLimitHint()
        this.hideStatus()
      }
    }
  }

  startRecordingTimer() {
    this.clearRecordingTimer()
    this.recordingTimer = window.setInterval(() => {
      if (!this.recording) return
      if (this.recordingDurationSeconds() >= this.constructor.MAX_RECORDING_SECONDS) {
        this.stopRecording({ reason: "limit" })
        return
      }
      this.showStatus(this.formatRecordingStatus(), { pulse: true })
    }, 1000)
  }

  clearRecordingTimer() {
    if (!this.recordingTimer) return
    window.clearInterval(this.recordingTimer)
    this.recordingTimer = null
  }

  formatRecordingStatus() {
    const elapsedSeconds = this.recordingDurationSeconds()
    const minutes = Math.floor(elapsedSeconds / 60).toString().padStart(2, "0")
    const seconds = (elapsedSeconds % 60).toString().padStart(2, "0")
    return `Recording ${minutes}:${seconds}`
  }

  recordingDurationSeconds() {
    return this.recordingStartedAt ? Math.max(0, Math.floor((Date.now() - this.recordingStartedAt) / 1000)) : 0
  }

  showStatus(message, { pulse = false, error = false } = {}) {
    if (!this.hasStatusTarget || !this.hasStatusTextTarget) return
    this.statusTextTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
    this.statusTarget.classList.add("inline-flex")
    this.statusTarget.classList.toggle("text-error-600", error)
    this.statusTarget.classList.toggle("text-[#6B7280]", !error)

    if (this.hasStatusDotTarget) {
      this.statusDotTarget.classList.toggle("hidden", !pulse)
    }
  }

  hideStatus() {
    if (!this.hasStatusTarget) return
    this.voiceErrorActive = false
    this.statusTarget.classList.add("hidden")
    this.statusTarget.classList.remove("inline-flex")
    if (this.hasStatusDotTarget) this.statusDotTarget.classList.add("hidden")
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = ""
  }

  showLimitHint() {
    if (!this.hasLimitHintTarget) return
    this.limitHintTarget.classList.remove("hidden")
  }

  hideLimitHint() {
    if (!this.hasLimitHintTarget) return
    this.limitHintTarget.classList.add("hidden")
  }

  audioFormatFor(mimeType) {
    const value = (mimeType || "").toLowerCase()

    if (value.includes("webm")) return "webm"
    if (value.includes("mp4")) return "mp4"
    if (value.includes("ogg")) return "ogg"
    if (value.includes("wav")) return "wav"
    if (value.includes("mpeg") || value.includes("mp3")) return "mp3"
    if (value.includes("m4a")) return "m4a"
    if (value.includes("flac")) return "flac"

    return null
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        // Strip the data URL prefix (e.g., "data:audio/wav;base64,")
        const base64 = reader.result.split(",")[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  isRefusalResponse(text) {
    const lower = text.toLowerCase()
    return (
      lower.includes("i'm sorry") ||
      lower.includes("i can't") ||
      lower.includes("i cannot") ||
      lower.includes("can't directly transcribe") ||
      lower.includes("cannot directly transcribe") ||
      lower.includes("unable to transcribe")
    )
  }

  insertText(text) {
    if (!this.hasInputTarget || this.disconnected || this.inputTarget.disabled) return

    const textarea = this.inputTarget
    const currentValue = textarea.value
    const separator = currentValue && !currentValue.endsWith(" ") ? " " : ""
    textarea.value = currentValue + separator + text

    // Trigger auto-resize and update submit button state
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    textarea.focus()
  }

  updateButtonState() {
    if (!this.hasButtonTarget) return

    if (this.recording) {
      this.buttonTarget.classList.add("bg-error-50", "px-3")
      this.buttonTarget.classList.remove("hover:bg-[#F2F3F5]", "w-10")
      if (this.hasIdleIconTarget) this.idleIconTarget.classList.add("hidden")
      if (this.hasRecordingIconTarget) {
        this.recordingIconTarget.classList.remove("hidden")
        this.recordingIconTarget.classList.add("inline-flex")
      }
      if (this.hasStopLabelTarget) {
        this.stopLabelTarget.classList.remove("hidden")
        this.stopLabelTarget.classList.add("inline-flex")
      }
    } else {
      this.buttonTarget.classList.remove("bg-error-50", "px-3")
      this.buttonTarget.classList.add("hover:bg-[#F2F3F5]", "w-10")
      if (this.hasIdleIconTarget) this.idleIconTarget.classList.remove("hidden")
      if (this.hasRecordingIconTarget) {
        this.recordingIconTarget.classList.add("hidden")
        this.recordingIconTarget.classList.remove("inline-flex")
      }
      if (this.hasStopLabelTarget) {
        this.stopLabelTarget.classList.add("hidden")
        this.stopLabelTarget.classList.remove("inline-flex")
      }
    }
  }

  setProcessingState(processing) {
    if (!this.hasButtonTarget) return

    if (processing) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("opacity-50")
    } else {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove("opacity-50")
    }
  }

  userFriendlyTranscriptionError(message, status) {
    const normalized = (message || "").toLowerCase()

    if (status === 413) return "The recording is too large."
    if (normalized.includes("timed out")) return "Voice transcription timed out."
    if (normalized.includes("api key")) return "Voice transcription isn't configured."
    if (normalized.includes("empty transcription")) return "No speech was detected."

    return "Couldn't transcribe the recording."
  }

  userFriendlyProcessingError(error) {
    const name = error?.name

    if (name === "EncodingError" || name === "NotReadableError") {
      return "Couldn't process the recording."
    }

    if (name === "AbortError") {
      return "Recording was interrupted."
    }

    return "Network or audio processing failed."
  }

  async reportClientError(message, { stage = "unknown", details = {} } = {}) {
    if (this.hasEndpointValue) return
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content

    try {
      await fetch(`/conversations/${this.conversationIdValue}/transcriptions/client_error`, {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          error: {
            message,
            stage,
            details,
          },
        }),
      })
    } catch (reportingError) {
      console.error("Failed to report voice client error:", reportingError)
    }
  }

  handleVoiceError(message, { stage = "unknown", details = {} } = {}) {
    this.recording = false
    this.voiceErrorActive = true
    this.clearRecordingTimer()
    this.setProcessingState(false)
    this.hideLimitHint()
    this.showStatus(message, { pulse: false, error: true })
    this.updateButtonState()
    this.reportClientError(message, { stage, details })

    if (window.toastController?.error) {
      window.toastController.error(message)
    } else {
      console.error(message)
    }
  }
}
